import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, FolderKanban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { Grid } from '@/components/layout/Grid';
import { ProjectCard } from '@/features/projects/components/ProjectCard';
import { ProjectForm } from '@/features/projects/components/ProjectForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';

export function ProjectsPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  const { data: projects, isLoading, error, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project')
        .select('id, name, description, status, target_date')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const handleCreate = async (values: { name: string; description?: string; status: string; start_date?: string; target_date?: string }) => {
    await supabase.from('project').insert({
      name: values.name,
      description: values.description ?? '',
      status: values.status,
      start_date: values.start_date || null,
      target_date: values.target_date || null,
    } as never);
    setShowCreate(false);
    refetch();
  };

  return (
    <div>
      <PageHeader
        title="Projects"
        action={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Project
          </Button>
        }
      />

      {error && <ErrorAlert message={String(error)} onRetry={() => refetch()} />}

      {isLoading && (
        <Grid cols={3}>
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-36" />)}
        </Grid>
      )}

      {!isLoading && projects?.length === 0 && (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description="Create a project to organize your work."
          actionLabel="Create Project"
          onAction={() => setShowCreate(true)}
        />
      )}

      {projects && projects.length > 0 && (
        <Grid cols={3}>
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} onClick={() => navigate(`/projects/${p.id}`)} />
          ))}
        </Grid>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
          <ProjectForm onSubmit={handleCreate} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
